import { useRef, useEffect, useCallback } from "react";

type UseScrollAnimationOptions = {
  readonly status: "idle" | "editing" | "playing" | "paused";
  readonly scrollSpeed: number;
  readonly initialOffset: number;
  readonly onReachEnd: () => void;
  readonly onOffsetChange: (offset: number) => void;
};

type UseScrollAnimationResult = {
  readonly textRef: React.RefObject<HTMLDivElement | null>;
  readonly containerRef: React.RefObject<HTMLDivElement | null>;
  readonly resetOffset: (offset: number) => void;
};

export const useScrollAnimation = ({
  status,
  scrollSpeed,
  initialOffset,
  onReachEnd,
  onOffsetChange,
}: UseScrollAnimationOptions): UseScrollAnimationResult => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef(initialOffset);
  const rafIdRef = useRef<number>(0);
  const lastTimestampRef = useRef<number>(0);

  const applyTransform = useCallback((offset: number) => {
    if (textRef.current) {
      textRef.current.style.transform = `translateY(-${offset}px)`;
    }
  }, []);

  // Sync initial offset when resuming
  useEffect(() => {
    if (status === "playing") {
      offsetRef.current = initialOffset;
      applyTransform(initialOffset);
    }
  }, [status, initialOffset, applyTransform]);

  // Main animation loop
  useEffect(() => {
    if (status !== "playing") {
      // On pause, save current offset
      if (status === "paused") {
        onOffsetChange(offsetRef.current);
      }
      return;
    }

    lastTimestampRef.current = 0;

    const tick = (timestamp: number) => {
      if (lastTimestampRef.current === 0) {
        lastTimestampRef.current = timestamp;
        rafIdRef.current = requestAnimationFrame(tick);
        return;
      }

      const delta = (timestamp - lastTimestampRef.current) / 1000;
      lastTimestampRef.current = timestamp;

      offsetRef.current += scrollSpeed * delta;

      // Check if text has scrolled past the end
      const textEl = textRef.current;
      const containerEl = containerRef.current;
      if (textEl && containerEl) {
        const maxScroll = textEl.scrollHeight - containerEl.clientHeight;
        if (maxScroll > 0 && offsetRef.current >= maxScroll) {
          offsetRef.current = maxScroll;
          applyTransform(offsetRef.current);
          onReachEnd();
          return;
        }
      }

      applyTransform(offsetRef.current);
      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
    };
  }, [status, scrollSpeed, onReachEnd, onOffsetChange, applyTransform]);

  const resetOffset = useCallback(
    (offset: number) => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
      offsetRef.current = offset;
      applyTransform(offset);
    },
    [applyTransform],
  );

  return { textRef, containerRef, resetOffset };
};
